import { useCallback, useEffect, useState } from 'react';
import { useGetCheckoutSessionQuery } from '../store';

const tokenKey = (eventId: string) => `${eventId}_checkout_token`;
export const RESUME_PARAM = 's';

export const storeCheckoutToken = (eventId: string, token: string) => {
  try {
    localStorage.setItem(tokenKey(eventId), token);
  } catch {
    // Sin storage la compra sigue; solo no se puede retomar al recargar.
  }
};

export const readCheckoutToken = (eventId: string): string | null => {
  try {
    return localStorage.getItem(tokenKey(eventId));
  } catch {
    return null;
  }
};

export const clearCheckoutToken = (eventId: string) => {
  try {
    localStorage.removeItem(tokenKey(eventId));
  } catch {
    // Nada que limpiar.
  }
};

interface CheckoutSessionTarget {
  id: string;
  business_id: string;
}

// El link del recordatorio trae ?s=<token>; si no, el que quedó guardado.
const findToken = (eventId: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const fromUrl = new URLSearchParams(window.location.search).get(RESUME_PARAM);
  if (fromUrl) {
    storeCheckoutToken(eventId, fromUrl);
    return fromUrl;
  }
  return readCheckoutToken(eventId);
};

/**
 * La compra en curso del checkout nuevo vive en el servidor: este hook
 * encuentra su token (el link del recordatorio trae ?s=, si no el que quedó
 * guardado en este navegador) y la lee. Recargar o volver con Atrás la
 * retoma donde estaba.
 */
const useCheckoutSession = (event: CheckoutSessionTarget | null | undefined) => {
  // Se lee en el primer render, no en un efecto: una pantalla que decide "no
  // hay compra en curso" no puede ver un token vacío que todavía no se cargó.
  const [override, setOverride] = useState<{ eventId: string, token: string | null } | null>(null);
  let token: string | null = null;
  if (event?.id) {
    token = override?.eventId === event.id ? override.token : findToken(event.id);
  }

  const query = useGetCheckoutSessionQuery(
    { businessId: event?.business_id ?? '', eventId: event?.id ?? '', token: token ?? '' },
    { skip: !event?.id || !token },
  );

  const remember = useCallback((next: string) => {
    if (event?.id) {
      storeCheckoutToken(event.id, next);
      setOverride({ eventId: event.id, token: next });
    }
  }, [event?.id]);

  const forget = useCallback(() => {
    if (event?.id) {
      clearCheckoutToken(event.id);
      setOverride({ eventId: event.id, token: null });
    }
  }, [event?.id]);

  // Una sesión que el servidor ya no reconoce (404) o que se cobró no se retoma.
  const notFound = !!query.error && 'status' in query.error && query.error.status === 404;
  useEffect(() => {
    if (notFound) {
      forget();
    }
  }, [notFound, forget]);

  return {
    token,
    session: token ? query.data ?? null : null,
    isLoading: !!token && query.isLoading,
    isError: !!query.error && !notFound,
    refetch: query.refetch,
    remember,
    forget,
  };
};

export default useCheckoutSession;
